import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  Button,
  Box,
  Text,
  Flex,
} from '@chakra-ui/react';
import React, { useState } from 'react';
import { MdDownload } from 'react-icons/md';
import { LeaveBalance } from '../../types/userTypes';
import { useGetAllEmployeeLeaveBalancesQuery } from '../../features/apis/authApi';
import ExcelJS from 'exceljs';

interface Props {
  isOpen: boolean;
  setIsOpen: (val: boolean) => void;
}

const LeaveDownload: React.FC<Props> = ({ isOpen, setIsOpen }) => {
  const [downloading, setDownloading] = useState(false);

  const { data = [], isLoading } = useGetAllEmployeeLeaveBalancesQuery(
    undefined,
    { skip: !isOpen }
  );

  const handleClose = () => setIsOpen(false);

  const leaveTypes: (keyof LeaveBalance)[] = [
    'general',
    'optional',
    'bereavement',
    'paternity',
    'maternity',
    'marriage',
    'anniversary',
    'birthday',
    'sick',
    'other',
  ];

  const handleDownload = async () => {
    try {
      setDownloading(true);

      const workbook = new ExcelJS.Workbook();
      const sheet = workbook.addWorksheet('Leave Balances');

      sheet.columns = [
        {
          header: 'Employee',
          key: 'employee',
          width: 25,
        },
        {
          header: 'Email',
          key: 'email',
          width: 30,
        },
        ...leaveTypes.map((t) => ({
          header: String(t).toUpperCase(),
          key: t,
          width: 12,
        })),
      ];

      data.forEach((emp: LeaveBalance) => {
        sheet.addRow({
          employee: emp.employeeName,
          email: emp.email || '',
          general: emp.general,
          optional: emp.optional,
          bereavement: emp.bereavement,
          paternity: emp.paternity,
          maternity: emp.maternity,
          marriage: emp.marriage,
          anniversary: emp.anniversary,
          birthday: emp.birthday,
          sick: emp.sick,
          other: emp.other,
        });
      });

      const headerRow = sheet.getRow(1);
      headerRow.font = { bold: true };

      const buffer = await workbook.xlsx.writeBuffer();

      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');

      const today = new Date().toISOString().split('T')[0];

      a.href = url;
      a.download = `leave-balances-${today}.xlsx`;
      a.click();

      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size={{ base: 'full', md: '5xl' }}
      isCentered
    >
      <ModalOverlay
        bg="blackAlpha.200"
        backdropFilter="blur(4px)"
      />

      <ModalContent
        className="!w-[1200px] !max-w-[95vw] !h-auto !max-h-[85vh] !overflow-hidden"
        height="auto"
        borderWidth="0.5px"
        m={0}
        borderColor="#D4D4D4"
      >
        <ModalCloseButton
          mr={3}
          mt={3}
          zIndex={2}
          sx={{
            _hover: {
              bg: 'transparent',
              color: 'black',
            },
          }}
        />

        <ModalBody
          display="flex"
          flexDirection="column"
          p={0}
          height="100%"
          overflow="hidden"
        >
          <Text
            fontSize="md"
            fontWeight="semibold"
            pl={4}
            pt={4}
            my={6}
          >
            Leave Balances
          </Text>

          <Box
            overflowX="auto"
            overflowY="auto"
            p={4}
            flex="1"
            width="100%"
          >
            <table className="w-full min-w-max border-collapse text-[11px]">
              <thead className="bg-[#E8E0FF]">
                <tr>
                  <th className="text-left px-3 py-3 text-xs text-[#444444] whitespace-nowrap">
                    Employee
                  </th>

                  <th className="text-left px-3 py-3 text-xs text-[#444444] whitespace-nowrap">
                    Email
                  </th>

                  {leaveTypes.map((type) => (
                    <th
                      key={type}
                      className="text-center px-3 py-3 text-xs text-[#444444] whitespace-nowrap"
                    >
                      {type}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody className="text-[11px]">
                {isLoading ? (
                  [...Array(4)].map((_, rowIndex) => (
                    <tr
                      key={rowIndex}
                      className="border-b-[0.5px] border-[#D4D4D4]"
                    >
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full bg-gray-200 animate-pulse shrink-0" />

                          <div className="space-y-2">
                            <div className="h-3 w-24 bg-gray-200 rounded animate-pulse" />
                            <div className="h-2 w-16 bg-gray-100 rounded animate-pulse" />
                          </div>
                        </div>
                      </td>

                      <td className="px-3 py-2">
                        <div className="h-3 w-32 bg-gray-200 rounded animate-pulse" />
                      </td>

                      {leaveTypes.map((type) => (
                        <td
                          key={type}
                          className="px-3 py-2 text-center"
                        >
                          <div className="h-3 w-10 bg-gray-200 rounded animate-pulse mx-auto" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : data.length === 0 ? (
                  <tr>
                    <td
                      colSpan={leaveTypes.length + 2}
                      className="text-center py-6"
                    >
                      <Text fontSize="sm" color="gray.500">
                        No leave balance data available
                      </Text>
                    </td>
                  </tr>
                ) : (
                  data.map((emp, i) => (
                    <tr
                      key={i}
                      className="hover:bg-gray-50 border-b-[0.5px] border-[#D4D4D4]"
                    >
                      <td className="px-3 py-2 text-left font-medium whitespace-nowrap">
                        {emp.employeeName}
                      </td>

                      <td className="px-3 py-2 text-left whitespace-nowrap">
                        {emp.email || '-'}
                      </td>

                      {leaveTypes.map((type) => (
                        <td
                          key={type}
                          className="px-3 py-2 text-center whitespace-nowrap"
                        >
                          {emp[type] || 0}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </Box>

          <Flex
            justify="flex-end"
            mt={2}
            mb={2}
            pr={4}
          >
            <Button
              borderRadius="6px"
              bgGradient="linear(to-r, #6535EB, #9F3198)"
              color="white"
              height={{ base: '26px', md: '34px' }}
              px={{ base: 3, md: 5 }}
              fontSize={{ base: '10px', md: '12px' }}
              fontWeight="500"
              whiteSpace="nowrap"
              transition="all 0.2s ease"
              _hover={{
                transform: 'translateY(-1px)',
                bgGradient: 'linear(to-r, #5a2fe0, #8a2a85)',
              }}
              _active={{
                transform: 'scale(0.98)',
              }}
              isLoading={downloading}
              loadingText="Preparing..."
              leftIcon={<MdDownload size={12} />}
              onClick={handleDownload}
            >
              Download
            </Button>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default LeaveDownload;